import { Interface } from '@ethersproject/abi'
import { keccak256 } from '@ethersproject/keccak256'

import V3StakerAbi from '../abis/v3-staker.json'
import { SupportedChainId } from '../constants/chains'
import { INCENTIVES, TESTNET_INCENTIVES } from '../constants/incentives'
import { Incentive } from '../types/incentives'

// Type for IncentiveKey interface matching the contract structure
type IncentiveData = {
  rewardToken: string
  pool: string
  startTime: number | string
  endTime: number | string
  refundee: string
}

/**
 * Encodes an incentive key as hex data for use with the V3Staker contract
 * @param data The incentive key data
 * @returns Hex string of the encoded incentive data
 */
export function getIncentiveKeyEncoded(data: IncentiveData) {
  const iface = new Interface(V3StakerAbi)
  const hex = iface.encodeFunctionData('endIncentive', [data]).substring(10)

  return '0x' + hex
}

/**
 * Generates a unique ID for an incentive based on its key data
 * @param data The incentive key data
 * @returns Hex string ID for the incentive
 */
export function getIncentiveId(data: IncentiveData) {
  const hex = getIncentiveKeyEncoded(data)

  const id = keccak256(hex)
  return id.substring(2)
}

/**
 * Helper function to check if an incentive is currently active
 * @param incentive The incentive to check
 * @returns boolean indicating if the incentive is currently active
 */
// eslint-disable-next-line import/no-unused-modules
export function isIncentiveActive(incentive: Incentive): boolean {
  const now = Math.floor(Date.now() / 1000)
  return incentive.key.startTime <= now && incentive.key.endTime > now
}

/**
 * Helper function to get the status of an incentive
 * @param incentive The incentive to check
 * @returns 'active', 'upcoming', or 'expired'
 */
export function getIncentiveStatus(incentive: Incentive): 'active' | 'upcoming' | 'expired' {
  const now = Math.floor(Date.now() / 1000)

  if (incentive.key.endTime < now) {
    return 'expired'
  } else if (incentive.key.startTime > now) {
    return 'upcoming'
  } else {
    return 'active'
  }
}

/**
 * Retrieves all active incentives from a list
 * @param incentives The list of incentives to filter
 * @returns Array of active incentives
 */
// eslint-disable-next-line import/no-unused-modules
export function getActiveIncentives(incentives: Incentive[]): Incentive[] {
  const now = Math.floor(Date.now() / 1000)
  return incentives.filter((incentive) => incentive.key.startTime <= now && incentive.key.endTime > now)
}

/**
 * @deprecated Use the useIncentivesQuery hook instead
 * Returns the incentives for the specified chain ID
 * @param chainId The chain ID to get incentives for
 * @returns Array of incentives for the specified chain
 */
// eslint-disable-next-line import/no-unused-modules
export function getIncentives(chainId: SupportedChainId | undefined): Incentive[] {
  switch (chainId) {
    case SupportedChainId.HYDRA:
    case undefined:
      return INCENTIVES

    case SupportedChainId.TESTNET:
      return TESTNET_INCENTIVES

    default:
      return []
  }
}
